import axios from 'axios';
import { 
  FormDefinition, 
  FormSubmission, 
  CreateFormRequest, 
  UpdateFormRequest, 
  SubmitFormRequest,
  FormFilters 
} from '../types/forms';
import { LOAN_APPLICATION_TEMPLATE } from '@/data/loanApplicationTemplate';
import { Logger } from '../utils/logger';

const API_BASE_URL = import.meta.env.VITE_API_URL;
const USE_MOCK_DATA = import.meta.env.VITE_ENVIRONMENT === 'development' && !import.meta.env.VITE_USE_REAL_API;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export class FormService {
  // Form CRUD operations
  static async createForm(formData: CreateFormRequest): Promise<FormDefinition> {
    const response = await api.post('/forms', formData);
    return response.data;
  }

  static async getForms(filters?: FormFilters): Promise<FormDefinition[]> {
    if (USE_MOCK_DATA) {
      // Return mock data or handle accordingly
      return [LOAN_APPLICATION_TEMPLATE];
    }
    try {
      const params = new URLSearchParams();
      if (filters?.category) params.append('category', filters.category);
      if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());
      if (filters?.version) params.append('version', filters.version);

      const response = await api.get(`/forms?${params.toString()}`);
      const data = response.data;
      
      // Ensure we always return an array
      return Array.isArray(data) ? data : [];
    } catch (error) {
      Logger.apiError('Failed to fetch forms', error as Error, {
        operation: 'getForms',
        filters
      });
      return []; // Return empty array on error
    }
  }

  static async getForm(id: string): Promise<FormDefinition> {
    const response = await api.get(`/forms/${id}`);
    return response.data;
  }

  static async updateForm(id: string, updateData: UpdateFormRequest): Promise<FormDefinition> {
    const response = await api.put(`/forms/${id}`, updateData);
    return response.data;
  }

  static async deleteForm(id: string): Promise<void> {
    await api.delete(`/forms/${id}`);
  }

  // Form submission operations
  static async submitForm(submissionData: SubmitFormRequest): Promise<{
    submissionId: string;
    status: string;
    validationErrors?: Record<string, string[]>;
    rulesExecuted?: string[];
  }> {
    const response = await api.post('/forms/submit', submissionData);
    return response.data;
  }

  static async getFormSubmissions(formId?: string, filters?: {
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<FormSubmission[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);

    const url = formId 
      ? `/forms/${formId}/submissions?${params.toString()}`
      : `/submissions?${params.toString()}`;
    
    const response = await api.get(url);
    return response.data;
  }

  // Form preview/validation operations
  static async validateFormData(formId: string, data: Record<string, unknown>): Promise<{
    isValid: boolean;
    errors?: Record<string, string[]>;
  }> {
    try {
      // For validation, we can use the submit endpoint in dry-run mode
      // or create a dedicated validation endpoint
      const form = await this.getForm(formId);
      
      // Client-side validation (similar to backend logic)
      const errors: Record<string, string[]> = {};
      
      for (const field of form.fields) {
        const value = data[field.id];
        const fieldErrors: string[] = [];
        
        // Check required fields
        if (field.required && (value === undefined || value === null || value === '')) {
          fieldErrors.push(`${field.label} is required`);
        }
        
        // Skip further validation if field is empty and not required
        if (!value && !field.required) continue;
        
        // Type-specific validation
        if (field.type === 'email' && value) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (typeof value === 'string' && !emailRegex.test(value)) {
            fieldErrors.push(`${field.label} must be a valid email address`);
          }
        }
        
        if (field.type === 'phone' && value) {
          const phoneRegex = /^\+?[\d\s-()]+$/;
          if (typeof value === 'string' && !phoneRegex.test(value)) {
            fieldErrors.push(`${field.label} must be a valid phone number`);
          }
        }
        
        if (field.type === 'number' && value) {
          if (isNaN(Number(value))) {
            fieldErrors.push(`${field.label} must be a number`);
          }
        }
        
        // Validation rules
        if (field.validation && value) {
          const validation = field.validation;
          const stringValue = typeof value === 'string' ? value : String(value);
          
          if (validation.minLength && stringValue.length < validation.minLength) {
            fieldErrors.push(`${field.label} must be at least ${validation.minLength} characters`);
          }
          
          if (validation.maxLength && stringValue.length > validation.maxLength) {
            fieldErrors.push(`${field.label} must be no more than ${validation.maxLength} characters`);
          }
          
          if (validation.min && Number(value) < validation.min) {
            fieldErrors.push(`${field.label} must be at least ${validation.min}`);
          }
          
          if (validation.max && Number(value) > validation.max) {
            fieldErrors.push(`${field.label} must be no more than ${validation.max}`);
          }
          
          if (validation.pattern) {
            const regex = new RegExp(validation.pattern);
            if (!regex.test(stringValue)) {
              fieldErrors.push(`${field.label} format is invalid`);
            }
          }
        }
        
        if (fieldErrors.length > 0) {
          errors[field.id] = fieldErrors;
        }
      }
      
      return {
        isValid: Object.keys(errors).length === 0,
        errors: Object.keys(errors).length > 0 ? errors : undefined
      };
    } catch (error) {
      Logger.error('Form validation failed', error as Error, {
        operation: 'validateFormData',
        formId,
        dataKeys: typeof data === 'object' && data ? Object.keys(data) : []
      });
      return { isValid: false, errors: { _form: ['Validation failed'] } };
    }
  }

  // Utility methods
  static async duplicateForm(id: string, newName: string): Promise<FormDefinition> {
    const existingForm = await this.getForm(id);
    const duplicateData: CreateFormRequest = {
      name: newName,
      description: `Copy of ${existingForm.description || existingForm.name}`,
      category: existingForm.category,
      fields: existingForm.fields,
      styling: existingForm.styling,
      notifications: existingForm.notifications
    };
    
    return this.createForm(duplicateData);
  }

  static async publishForm(id: string): Promise<FormDefinition> {
    return this.updateForm(id, { isActive: true });
  }

  static async unpublishForm(id: string): Promise<FormDefinition> {
    return this.updateForm(id, { isActive: false });
  }
}

export default FormService;
